import { ColumnDef } from "@tanstack/react-table";
import AdvanceTable from "components/base/AdvanceTable";
import AdvanceTableFooter from "components/base/AdvanceTableFooter";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Badge, { BadgeBg } from "components/base/Badge";
import StarCheckbox from "components/base/StarCheckbox";
import RevealDropdown, {
  RevealDropdownTrigger,
} from "components/base/RevealDropdown";
import ActionDropdownItems from "components/custom/ActionDropdownItems";
import { currencyFormat } from "helpers/utils";
import { ProductsTableProductType, Status } from "pages/apps/e-commerce/admin/Products";
import { ShippingMethod } from "components/modals/CommissionDetailsModal";
import { useContext, createContext, useEffect, useState, useMemo } from "react";

// Create context for sharing the callback functions
export const AdvanceTableContext = createContext<{
  props?: {
    onShowReasonDetails?: (details: { reason: string[]; description: string }) => void;
    onShowCommissionDetails?: (product: ProductsTableProductType) => void;
  };
} | null>(null);

// Interface for props with optional callback functions
interface ProductsTableProps {
  totalCount?: number;
  onShowReasonDetails?: (details: { reason: string[]; description: string }) => void;
  onShowCommissionDetails?: (product: ProductsTableProductType) => void;
}

// Main component definition
const ProductsTable = ({ totalCount, onShowReasonDetails, onShowCommissionDetails }: ProductsTableProps) => {
  const { t } = useTranslation();

  // Use useMemo to create translated column definitions
  // This ensures they're only recreated when the language changes
  const translatedColumns = useMemo<ColumnDef<ProductsTableProductType>[]>(() => [
    //  1) Product Image and Product Name
    {
      id: "product",
      accessorKey: "product",
      header: t("products.details"),
      cell: ({ row: { original } }) => {
        const { product, productImage, sku, sellerReference, type, visibility } = original;
        const navigate = useNavigate();
        return (
          <div className="d-flex align-items-center">
            {/* Product Image */}
            <img
              src={productImage}
              alt={product}
              width={53}
              height={53}
              className="me-3 border border-translucent rounded-2"
              style={{ objectFit: "contain" }}
            />

            {/* Product Information */}
            <div className="d-flex flex-column">
              <span 
                className="fw-semibold line-clamp-3 text-body-highlight cursor-pointer" 
                onClick={() => navigate(`/products/${original.id}`)}
                style={{ textDecoration: 'underline' }}
              >
                {product}
              </span>

              {/* SKU */}
              <span className="text-body-secondary fs-10">{t("sku")}: {sku}</span>

              {/* Seller Reference */}
              <span className="text-body-highlight fs-10">
                {t("sellerReference")}: {sellerReference || t("notAvailable")}
              </span>
            </div>
          </div>
        );
      },
      meta: {
        headerProps: { style: { width: 350 }, className: "ps-4" },
        cellProps: { className: "ps-4" },
      },
    },
    // 2) Add a new column for the product type
    {
      id: "type",
      accessorKey: "type",
      header: t("type"),
      cell: ({ row: { original } }) => {
        const { type, visibility } = original;

        // Determine badge variant and display text based on type and visibility
        let variant: BadgeBg;
        let displayText: string;

        if (type === "configurable") {
          variant = "primary";
          displayText = t("configurable");
        } else if (type === "simple" && visibility === 1) {
          variant = "info"; // Use a distinct color for Variant
          displayText = t("variant");
        } else {
          variant = "secondary";
          displayText = t("simple");
        }

        return <Badge bg={variant} variant="phoenix">{displayText}</Badge>;
      },
      meta: {
        headerProps: { style: { width: 200 }, className: "ps-4" },
        cellProps: { className: "ps-4 text-center" },
      },
    },
    // 3) Add a new column for the product variant
    {
      id: "variant",
      accessorKey: "variant",
      header: t("variant"),
      cell: ({ row: { original } }) => (original.variant),
      meta: {
        headerProps: { style: { width: 200 }, className: "ps-4 " },
        cellProps: { className: "fw-semibold ps-4 text-body-highlight text-center" },
      },
    },
    // 4) Add a new column for the product situation
    {
      id: "situation",
      accessorKey: "situation",
      header: t("situation"),
      cell: ({ row: { original } }) => {
        const { status } = original;
        const displayText = status === 1 ? t("onSale") : t("closeForSale");
        const variant: BadgeBg = status === 1 ? "success" : "danger";

        return <Badge bg={variant} variant="phoenix">{displayText}</Badge>;
      },
      meta: {
        headerProps: { style: { width: 120 }, className: "ps-4" },
        cellProps: { className: "ps-4 text-center" },
      },
    },
    // 5) Add a new column for the product %commission
    {
      id: "percentCommission",
      accessorKey: "percentCommission",
      header: t("products.percentCommission"),
      cell: ({ row: { original } }) => {
        // Get the global table context
        const context = useContext(AdvanceTableContext);
        // Extract the onShowCommissionDetails function from the context
        const onShowCommissionDetails = context?.props?.onShowCommissionDetails;

        return (
          <span 
            className={onShowCommissionDetails ? "cursor-pointer text-decoration-underline" : ""}
            onClick={() => onShowCommissionDetails && onShowCommissionDetails(original)}
          >
            {original.percentCommission} %
          </span>
        );
      },
      meta: {
        headerProps: { style: { width: 120 }, className: "ps-4 " },
        cellProps: { className: "fw-semibold ps-4 text-body-highlight text-center " },
      },
    },
    // 6) Add a new column for the product fix commission
    {
      id: "fixCommission",
      accessorKey: "fixCommission",
      header: t("products.fixCommission"),
      cell: ({ row: { original } }) => {
        // Get the global table context
        const context = useContext(AdvanceTableContext);
        // Extract the onShowCommissionDetails function from the context
        const onShowCommissionDetails = context?.props?.onShowCommissionDetails;

        return (
          <span 
            className={onShowCommissionDetails ? "cursor-pointer text-decoration-underline" : ""}
            onClick={() => onShowCommissionDetails && onShowCommissionDetails(original)}
          >
            {currencyFormat(original.fixCommission ?? 0)}
          </span>
        );
      },
      meta: {
        headerProps: { style: { width: 120 }, className: "ps-4 " },
        cellProps: { className: "fw-semibold ps-4 text-body-highlight text-center " },
      },
    },
    // 7) Add a new column for the product price
    {
      id: "Price",
      accessorKey: "Price",
      header: t("price"),
      cell: ({ row: { original } }) => {
        // Get the global table context
        const context = useContext(AdvanceTableContext);
        // Extract the onShowCommissionDetails function from the context
        const onShowCommissionDetails = context?.props?.onShowCommissionDetails;

        return (
          <span 
            className={onShowCommissionDetails ? "cursor-pointer text-decoration-underline" : ""}
            onClick={() => onShowCommissionDetails && onShowCommissionDetails(original)}
          >
            {currencyFormat(original.price)}
          </span>
        );
      },
      meta: {
        headerProps: { style: { width: 150 }, className: "ps-4 " },
        cellProps: { className: "fw-semibold ps-4 text-body-highlight text-center " },
      },
    },
    // 8) Add a new column for the product special price
    {
      id: "specialPrice",
      accessorKey: "specialPrice",
      header: t("specialPrice"),
      cell: ({ row: { original } }) => {
        const { specialPrice, discountStartDate, discountEndDate } = original;

        // ✅ Ensure specialPrice is always a number
        const price = specialPrice ?? 0;

        // ✅ Format date function (fix TypeScript issue)
        const formatDate = (date: string | null | undefined): string =>
          date ? new Date(date).toLocaleDateString("fr-FR") : t("notAvailable");
          
        // Get the global table context
        const context = useContext(AdvanceTableContext);
        // Extract the onShowCommissionDetails function from the context
        const onShowCommissionDetails = context?.props?.onShowCommissionDetails;

        return (
          <div 
            className={`d-flex flex-column text-center ${onShowCommissionDetails && price > 0 ? "cursor-pointer" : ""}`}
            onClick={() => price > 0 && onShowCommissionDetails && onShowCommissionDetails(original)}
          >
            {/* ✅ Special Price - Show "-" when missing or 0 */}
            <span className={`fw-semibold text-body-highlight ${onShowCommissionDetails && price > 0 ? "text-decoration-underline" : ""}`}>
              {price > 0 ? currencyFormat(price, { currency: "TND" }) : "-"}
            </span>

            {/* ✅ Show dates only if specialPrice > 0 */}
            {price > 0 && (
              <>
                <span className="text-body-secondary fs-10">
                  {t("products.from")}: {formatDate(discountStartDate)}
                </span>
                <span className="text-body-tertiary fs-10">
                  {t("products.to")}: {formatDate(discountEndDate)}
                </span>
              </>
            )}
          </div>
        );
      },
      meta: {
        headerProps: { style: { width: 200 }, className: "ps-4" },
        cellProps: { className: "fw-semibold ps-4 text-body-highlight text-center " },
      },
    },
    // 9) Add a new column for the product shipping method
    {
      id: "shippingMethod",
      accessorKey: "shippingMethod",
      header: t("products.shippingMethod"),
      cell: ({ row: { original } }) => {
        const { shippingMethod } = original;
        let displayText = "";
        let variant: BadgeBg = "secondary";
        if (shippingMethod === ShippingMethod.FBW) {
          displayText = t("shippingMethodType.fbw");
          variant = "success";
        } else if (shippingMethod === ShippingMethod.ESS) {
          displayText = t("shippingMethodType.ess");
          variant = "warning";
        } else if (shippingMethod === ShippingMethod.ESW) {
          displayText = t("shippingMethodType.esw");
          variant = "info";
        }
        return <Badge bg={variant} variant="phoenix">{displayText}</Badge>;
      },
      meta: {
        headerProps: { style: { width: 200 }, className: "ps-4" },
        cellProps: { className: "fs-9 fw-semibold ps-4 text-body-highlight text-opacity-85", },
      },
    },
    // 10) Add a new column for the product stock
    {
      id: "stock",
      accessorKey: "stock",
      header: t("stock"),
      cell: ({ row: { original } }) => {
        const { stock, shippingMethod } = original;
        const isEditable = shippingMethod === ShippingMethod.ESS || shippingMethod === ShippingMethod.ESW;
        return (
          <>
            <span>{stock}</span>
          </>
        );
      },
      meta: {
        headerProps: { style: { width: 100 }, className: "ps-4" },
        cellProps: { className: "fw-semibold ps-4 text-body-highlight text-center" },
      },
    },
    // 13) Add a new column for the product brand
    {
      id: "brand",
      accessorKey: "brand",
      header: t("brand"),
      cell: ({ row: { original } }) => original.brand === "Pas fournie" ? "-" : original.brand,
      meta: {
        headerProps: { style: { width: 150 }, className: "ps-4" },
        cellProps: {
          className: "fs-9 fw-semibold ps-4 text-body-highlight text-opacity-85",
        },
      },
    },
    // 14) Add a new column for the product category
    {
      id: "category",
      accessorKey: "category",
      header: t("category"),
      meta: {
        headerProps: { style: { width: 150 }, className: "ps-4" },
        cellProps: { className: "ps-4 text-center" },
      },
      cell: ({ row: { original } }) => {
        const { category } = original;

        return category?.length > 0 ? (
          <div className="d-flex flex-wrap gap-1">
            {category.map((cat: any, index) => (
              <Badge key={index} bg="secondary" variant="phoenix">
                {cat.name}
              </Badge>
            ))}
          </div>
        ) : (
          t("notAvailable")
        );
      },
    },
    // Add a new column for deactivation reasons
    {
      id: "deactivation_reason",
      accessorKey: "deactivation_reason",
      header: t("products.deactivationReasons"),
      meta: {
        headerProps: { style: { width: 180 }, className: "ps-4" },
        cellProps: { className: "ps-4 text-center" },
      },
      cell: ({ row: { original } }) => {
        const { deactivation_reason } = original;
        
        // Get the global table context
        const context = useContext(AdvanceTableContext);
        // Extract the onShowReasonDetails function from the context
        const onShowReasonDetails = context?.props?.onShowReasonDetails;

        const handleReasonsClick = () => {
          if (deactivation_reason && onShowReasonDetails) {
            onShowReasonDetails(deactivation_reason);
          }
        };

        // Check if deactivation_reason exists and has reasons array
        return deactivation_reason && deactivation_reason.reason && deactivation_reason.reason.length > 0 ? (
          <div className="d-flex flex-wrap gap-1">
            {deactivation_reason.reason.map((reasonText: string, index: number) => (
              <Badge 
                key={index} 
                bg="danger" 
                variant="phoenix"
                className="cursor-pointer"
                onClick={handleReasonsClick}
              >
                {reasonText}
              </Badge>
            ))}
          </div>
        ) : (
          "-"
        );
      },
    },
    // Add a new column for the product weight
    {
      id: "weight",
      accessorKey: "weight",
      header: t("weight"),
      cell: ({ row: { original } }) => (
        <span className="text-body-secondary fw-semibold">
          {original.weight !== "N/A" ? `${original.weight} kg` : t("notAvailable")}
        </span>
      ),
      meta: {
        headerProps: { style: { width: 120 }, className: "ps-4" },
        cellProps: { className: "ps-4 text-center" },
      },
    },
    // Add a new column for the product approval status
    {
      id: "product_status",
      accessorKey: "product_status",
      header: t("products.approvalStatus"),
      cell: ({ row: { original } }) => {
        const { product_status } = original;

        const displayText =
          product_status === 1 ? t("approved") : product_status === 3 ? t("rejected") : t("disapproved");
        const variant: BadgeBg =
          product_status === 1 ? "success" : product_status === 3 ? "danger" : "warning";

        return <Badge bg={variant} variant="phoenix">{displayText}</Badge>;
      },
      meta: {
        headerProps: { style: { width: 150 }, className: "ps-4" },
        cellProps: { className: "ps-4 text-center" },
      },
    },
    // Add a new column for the product action 
    {
      id: "action",
      cell: ({ row: { original } }) => ( // Access row data here
        <RevealDropdownTrigger>
          <RevealDropdown>
            <ActionDropdownItems productId={original.id} /> {/* Pass product ID correctly */}
          </RevealDropdown>
        </RevealDropdownTrigger>
      ),
      meta: {
        headerProps: { style: { width: "7%" } },
        cellProps: { className: "text-end" },
      },
    }
  ], [t]); // Add t to dependency array to update when language changes

  // Update the exported columns for use in Products.tsx
  useEffect(() => {
    // Update the exported columns
    productsTablecolumns.length = 0;
    productsTablecolumns.push(...translatedColumns);
  }, [translatedColumns]);

  return (
    <AdvanceTableContext.Provider value={{ props: { onShowReasonDetails, onShowCommissionDetails } }}>
      <div>
        {/* The table - don't pass columns directly */}
        <AdvanceTable 
          tableProps={{ className: "phoenix-table fs-9" }}
        />
        {/* Pagination */}
        <AdvanceTableFooter pagination totalCount={totalCount} />
      </div>
    </AdvanceTableContext.Provider>
  );
};

// Export the component
export default ProductsTable;

// Export the columns for use in the main Products component
export const productsTablecolumns: ColumnDef<ProductsTableProductType>[] = [];
